import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { currencyOptions } from 'constant';
import { TextareaInput, CurrencyInput } from 'components/FormControl';
import { toast } from 'layout';

export default function AdjustmentForm({ readOnly, control, errors, watch, setValue }) {
  // #region Data
  const { t } = useTranslation();
  const [total, setTotal] = useState({
    beforeExpenditureFromVNUHCM: 0,
    beforeExpenditureFromFunding: 0,
    afterExpenditureFromVNUHCM: 0,
    afterExpenditureFromFunding: 0,
  });
  // const firstChange = useState(true);

  const itemsWatch = watch('financialAdjustmentItems');
  const term1 = watch('financialAdjustmentItems.0');
  const term11 = watch('financialAdjustmentItems.1');
  const term12 = watch('financialAdjustmentItems.2');
  const term13 = watch('financialAdjustmentItems.3');

  useEffect(() => {
    // case 1: term 1 change less than term 11
    const totalTerm1SubsVNU =
      (term11?.afterExpenditureFromVNUHCM ?? 0) +
      (term12?.afterExpenditureFromVNUHCM ?? 0) +
      (term13?.afterExpenditureFromVNUHCM ?? 0);
    const totalTerm1SubsFunding =
      (term11?.afterExpenditureFromFunding ?? 0) +
      (term12?.afterExpenditureFromFunding ?? 0) +
      (term13?.afterExpenditureFromFunding ?? 0);
    if (term1?.afterExpenditureFromVNUHCM < totalTerm1SubsVNU) {
      const diff = totalTerm1SubsVNU - (term1?.afterExpenditureFromVNUHCM ?? 0);
      if (diff > term11?.afterExpenditureFromVNUHCM) {
        setValue(
          'financialAdjustmentItems.1.afterExpenditureFromVNUHCM',
          term1?.afterExpenditureFromVNUHCM ?? 0
        );
        setValue('financialAdjustmentItems.2.afterExpenditureFromVNUHCM', 0);
        setValue('financialAdjustmentItems.3.afterExpenditureFromVNUHCM', 0);
      }
      if (diff <= term11?.afterExpenditureFromVNUHCM) {
        setValue('financialAdjustmentItems.1.afterExpenditureFromVNUHCM', diff);
      }
    }
    if (term1?.afterExpenditureFromVNUHCM > totalTerm1SubsVNU) {
      const newValue =
        (term11?.afterExpenditureFromVNUHCM ?? 0) +
        (term1?.afterExpenditureFromVNUHCM ?? 0) -
        totalTerm1SubsVNU;
      setValue('financialAdjustmentItems.1.afterExpenditureFromVNUHCM', newValue);
    }
    if (term1?.afterExpenditureFromFunding < totalTerm1SubsFunding) {
      const diff = totalTerm1SubsFunding - (term1?.afterExpenditureFromFunding ?? 0);
      if (diff > 0) {
        setValue(
          'financialAdjustmentItems.1.afterExpenditureFromFunding',
          term1?.afterExpenditureFromFunding ?? 0
        );
        setValue('financialAdjustmentItems.2.afterExpenditureFromFunding', 0);
        setValue('financialAdjustmentItems.3.afterExpenditureFromFunding', 0);
      }
      if (diff <= 0) {
        setValue('financialAdjustmentItems.1.afterExpenditureFromFunding', diff);
      }
    }
    if (term1?.afterExpenditureFromFunding > totalTerm1SubsFunding) {
      const newValue =
        (term11?.afterExpenditureFromFunding ?? 0) +
        (term1?.afterExpenditureFromFunding ?? 0) -
        totalTerm1SubsFunding;
      setValue('financialAdjustmentItems.1.afterExpenditureFromFunding', newValue);
    }
  }, [JSON.stringify(term1)]);

  useEffect(() => {
    const totalTerm1SubsVNU =
      (term12?.afterExpenditureFromVNUHCM ?? 0) + (term13?.afterExpenditureFromVNUHCM ?? 0);
    const totalTerm1SubsFunding =
      (term12?.afterExpenditureFromFunding ?? 0) + (term13?.afterExpenditureFromFunding ?? 0);
    // term12 => term11 + term12 + term13 > term1
    const term1ExpenditureVNU = term1?.afterExpenditureFromVNUHCM;
    const term3ExpenditureVNU = term13?.afterExpenditureFromVNUHCM;
    if (term1ExpenditureVNU < totalTerm1SubsVNU) {
      setTimeout(() => {
        setValue('financialAdjustmentItems.2.afterExpenditureFromVNUHCM', 0);
        setValue(
          'financialAdjustmentItems.1.afterExpenditureFromVNUHCM',
          term1ExpenditureVNU - term3ExpenditureVNU
        );
      }, 1);

      toast('error', t('formControl.validation.canNotGreaterThanExpenditureFromVNU'));
    }
    if (term1ExpenditureVNU >= totalTerm1SubsVNU) {
      setValue(
        'financialAdjustmentItems.1.afterExpenditureFromVNUHCM',
        term1ExpenditureVNU - totalTerm1SubsVNU
      );
    }

    const term1ExpenditureFunding = term1?.afterExpenditureFromFunding;
    const term3ExpenditureFunding = term13?.afterExpenditureFromFunding;
    if (term1ExpenditureFunding < totalTerm1SubsFunding) {
      setTimeout(() => {
        setValue('financialAdjustmentItems.2.afterExpenditureFromFunding', 0);
        setValue(
          'financialAdjustmentItems.1.afterExpenditureFromFunding',
          term1ExpenditureFunding - term3ExpenditureFunding
        );
      }, 1);

      toast('error', t('formControl.validation.canNotGreaterThanExpenditureFromFunding'));
    }
    if (term1ExpenditureFunding >= totalTerm1SubsFunding) {
      setValue(
        'financialAdjustmentItems.1.afterExpenditureFromFunding',
        term1ExpenditureFunding - totalTerm1SubsFunding
      );
    }
  }, [JSON.stringify(term12)]);

  useEffect(() => {
    const totalTerm1SubsVNU =
      (term12?.afterExpenditureFromVNUHCM ?? 0) + (term13?.afterExpenditureFromVNUHCM ?? 0);
    const totalTerm1SubsFunding =
      (term12?.afterExpenditureFromFunding ?? 0) + (term13?.afterExpenditureFromFunding ?? 0);

    // term12 => term11 + term12 + term13 > term1
    const term1ExpenditureVNU = term1?.afterExpenditureFromVNUHCM;
    const term2ExpenditureVNU = term12?.afterExpenditureFromVNUHCM;
    if (term1ExpenditureVNU < totalTerm1SubsVNU) {
      setTimeout(() => {
        setValue('financialAdjustmentItems.3.afterExpenditureFromVNUHCM', 0);
        setValue(
          'financialAdjustmentItems.1.afterExpenditureFromVNUHCM',
          term1ExpenditureVNU - term2ExpenditureVNU
        );
      }, 1);

      toast('error', t('formControl.validation.canNotGreaterThanExpenditureFromVNU'));
    }
    if (term1ExpenditureVNU >= totalTerm1SubsVNU) {
      setValue(
        'financialAdjustmentItems.1.afterExpenditureFromVNUHCM',
        term1ExpenditureVNU - totalTerm1SubsVNU
      );
    }

    const term1ExpenditureFunding = term1?.afterExpenditureFromFunding;
    const term3ExpenditureFunding = term12?.afterExpenditureFromFunding;
    if (term1ExpenditureFunding < totalTerm1SubsFunding) {
      setTimeout(() => {
        setValue('financialAdjustmentItems.3.afterExpenditureFromFunding', 0);
        setValue(
          'financialAdjustmentItems.1.afterExpenditureFromFunding',
          term1ExpenditureFunding - term3ExpenditureFunding
        );
      }, 1);

      toast('error', t('formControl.validation.canNotGreaterThanExpenditureFromFunding'));
    }
    if (term1ExpenditureFunding >= totalTerm1SubsFunding) {
      setValue(
        'financialAdjustmentItems.1.afterExpenditureFromFunding',
        term1ExpenditureFunding - totalTerm1SubsFunding
      );
    }
  }, [JSON.stringify(term13)]);

  useEffect(() => {
    // setTimeout(() => {

    // }, 1);
    setTotal(() => ({
      beforeExpenditureFromVNUHCM: itemsWatch?.reduce(
        (acc, cur) =>
          cur?.subProposedExpenditureTypeId ? acc : acc + (cur?.beforeExpenditureFromVNUHCM || 0),
        0
      ),
      beforeExpenditureFromFunding: itemsWatch?.reduce(
        (acc, cur) =>
          cur?.subProposedExpenditureTypeId ? acc : acc + (cur?.beforeExpenditureFromFunding || 0),
        0
      ),

      afterExpenditureFromVNUHCM: itemsWatch?.reduce(
        (acc, cur) =>
          cur?.subProposedExpenditureTypeId ? acc : acc + (cur?.afterExpenditureFromVNUHCM || 0),
        0
      ),

      afterExpenditureFromFunding: itemsWatch?.reduce(
        (acc, cur) =>
          cur?.subProposedExpenditureTypeId ? acc : acc + (cur?.afterExpenditureFromFunding || 0),
        0
      ),
    }));
  }, [JSON.stringify(itemsWatch)]);

  return (
    <div className="form-table p-fluid">
      <table className="mb-2 w-full">
        <thead>
          <tr>
            <th className="text-center" style={{ minWidth: 50, width: 50 }}>
              {t('formLayout.numericalOrder')}
            </th>
            <th className="text-center" style={{ minWidth: 300, maxWidth: 300 }}>
              {t('financialAdjustment.table.name')}
            </th>
            <th style={{ border: 'none', padding: 0 }}>
              <table className="inner-table" style={{ minWidth: 300, height: 70 }}>
                <thead>
                  <tr>
                    <th colSpan={2}>{t('financialAdjustment.table.before')}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ minWidth: 150 }}>Từ ĐHQG-HCM</td>
                    <td style={{ minWidth: 150 }}>Từ nguồn huy động</td>
                  </tr>
                </tbody>
              </table>
            </th>
            <th style={{ border: 'none', padding: 0 }}>
              <table className="inner-table" style={{ minWidth: 300, height: 70 }}>
                <thead>
                  <tr>
                    <th colSpan={2}>{t('financialAdjustment.table.after')}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ minWidth: 150 }}>Từ ĐHQG-HCM</td>
                    <td style={{ minWidth: 150 }}>Từ nguồn huy động</td>
                  </tr>
                </tbody>
              </table>
            </th>

            <th className="text-center" style={{ minWidth: 200, width: '100%' }}>
              {t('financialAdjustment.table.reason')}
            </th>
          </tr>
        </thead>
        <tbody>
          {itemsWatch?.map((item, index) => (
            <tr key={item?.expenditureTypeId}>
              <td className="text-center">{index + 1}</td>
              <td>
                <TextareaInput
                  name={`financialAdjustmentItems.${index}.name`}
                  control={control}
                  errors={errors}
                  readOnly
                />
              </td>

              <td>
                <table style={{ border: 'none' }}>
                  <thead>
                    <tr>
                      <th
                        style={{
                          minWidth: 150,
                          padding: 0,
                          border: 'none',
                          paddingRight: 2,
                          width: '50%',
                        }}
                      >
                        <CurrencyInput
                          name={`financialAdjustmentItems.${index}.beforeExpenditureFromVNUHCM`}
                          control={control}
                          errors={errors}
                          readOnly
                          disabled={index === 1}
                        />
                      </th>
                      <th
                        style={{
                          minWidth: 150,
                          padding: 0,
                          border: 'none',
                          paddingLeft: 2,
                          width: '50%',
                        }}
                      >
                        <CurrencyInput
                          name={`financialAdjustmentItems.${index}.beforeExpenditureFromFunding`}
                          control={control}
                          errors={errors}
                          readOnly
                          disabled={index === 1}
                        />
                      </th>
                    </tr>
                  </thead>
                </table>
              </td>

              <td>
                <table style={{ border: 'none' }}>
                  <thead>
                    <tr>
                      <th
                        style={{
                          minWidth: 150,
                          padding: 0,
                          border: 'none',
                          paddingRight: 2,
                        }}
                      >
                        <CurrencyInput
                          name={`financialAdjustmentItems.${index}.afterExpenditureFromVNUHCM`}
                          control={control}
                          errors={errors}
                          disabled={index === 1}
                          readOnly={readOnly}
                        />
                      </th>
                      <th
                        style={{
                          minWidth: 150,
                          padding: 0,
                          border: 'none',
                          paddingLeft: 2,
                        }}
                      >
                        <CurrencyInput
                          name={`financialAdjustmentItems.${index}.afterExpenditureFromFunding`}
                          control={control}
                          errors={errors}
                          disabled={index === 1}
                          readOnly={readOnly}
                        />
                      </th>
                    </tr>
                  </thead>
                </table>
              </td>

              <td>
                <TextareaInput
                  name={`financialAdjustmentItems.${index}.reason`}
                  control={control}
                  errors={errors}
                  readOnly={readOnly}
                  disabled={index === 1}
                />
              </td>
            </tr>
          ))}

          <tr key={100} style={{ height: 36 }}>
            <td className="text-center" />
            <td className="font-bold">Tổng cộng</td>

            <td>
              <table style={{ border: 'none' }}>
                <thead>
                  <tr>
                    <th
                      className="text-right font-bold bg-white"
                      style={{
                        minWidth: 150,
                        padding: 0,
                        border: 'none',
                        paddingRight: 2,
                        borderRight: 1,
                      }}
                    >
                      {total.beforeExpenditureFromVNUHCM?.toLocaleString('vi', currencyOptions)}
                    </th>
                    <th
                      className="text-right font-bold bg-white"
                      style={{
                        minWidth: 150,
                        padding: 0,
                        border: 'none',
                        paddingLeft: 2,
                      }}
                    >
                      {total.beforeExpenditureFromFunding?.toLocaleString('vi', currencyOptions)}
                    </th>
                  </tr>
                </thead>
              </table>
            </td>

            <td>
              <table style={{ border: 'none', backgroundColor: '#fff' }}>
                <thead>
                  <tr>
                    <th
                      className="text-right font-bold bg-white"
                      style={{ minWidth: 150, padding: 0, border: 'none', paddingRight: 2 }}
                    >
                      {total.afterExpenditureFromVNUHCM?.toLocaleString('vi', currencyOptions)}
                    </th>
                    <th
                      style={{
                        minWidth: 150,
                        padding: 0,
                        border: 'none',
                        paddingLeft: 2,
                      }}
                      className="text-right font-bold bg-white"
                    >
                      {total.afterExpenditureFromFunding?.toLocaleString('vi', currencyOptions)}
                    </th>
                  </tr>
                </thead>
              </table>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

AdjustmentForm.propTypes = {
  readOnly: PropTypes.bool,
  errors: PropTypes.shape({}).isRequired,
  control: PropTypes.shape({}).isRequired,
  watch: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
};

AdjustmentForm.defaultProps = {
  readOnly: false,
};
